import React, { useEffect, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import NoteSection from "./MyNotes/NoteSection";
import ViewNoteModal from "./MyNotes/ViewNoteModal";
import EditNoteModal from "./MyNotes/EditNoteModal";
import NoteToast from "./MyNotes/NoteToast";
import NoteFilterPanel from "./MyNotes/NoteFilterPanel";
import MyNotesEmptyState from "./MyNotes/MyNotesEmptyState";
import {
  createEmptyNoteDraft,
  deriveNoteTitle,
  filterNotes,
  getSubjectTagStyle,
  getTagDisplayLabel,
  getUniqueTags,
  groupNotesBySection,
} from "../utils/myNotes";
import {
  createMyNote,
  deleteMyNote,
  getMyNotes,
  getMyNotesSubjects,
  searchMyNotes,
  updateMyNote,
  getFilteredNotesBySubjects,
} from "../services/myNotesService";
import "../InteractiveSyllabus.css";
import "../MyNotes.css";

function NotesSkeleton() {
  return (
    <div className="my-notes-skeleton">
      {[0, 1].map((section) => (
        <section className="my-notes-section" key={section}>
          <Skeleton width={180} height={18} />
          <div className="my-notes-section-row is-carousel">
            <div className="my-notes-section-cards is-scrollable">
              {[...Array(4)].map((_, index) => (
                <div className="skeleton-note-card my-notes-card" key={index}>
                  <span className="my-notes-card-accent" aria-hidden="true" />
                  <div className="my-notes-card-top">
                    <div className="my-notes-card-title-row">
                      <h3 style={{ width: "100%" }}>
                        <Skeleton width={150} height={14} />
                      </h3>
                    </div>
                    <div className="my-notes-card-menu">
                      <span
                        className="my-notes-card-menu-button skeleton-menu-button"
                        aria-hidden="true"
                      >
                        <Skeleton width={16} height={16} borderRadius="50%" />
                      </span>
                    </div>
                  </div>
                  <div className="my-notes-card-preview">
                    <Skeleton height={12} count={2} style={{ marginBottom: 6 }} />
                  </div>
                  <div className="my-notes-card-tags">
                    <Skeleton width={54} height={20} borderRadius={10} />
                    <Skeleton width={70} height={20} borderRadius={10} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}

function Mynotes({ Check, isSidebarColl }) {
  const [notes, setNotes] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [noteSubjects, setNoteSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewNote, setViewNote] = useState(null);
  const [editNote, setEditNote] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  const [pendingDeleteNote, setPendingDeleteNote] = useState(null);
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const [filteredByTags, setFilteredByTags] = useState(null);
  const [filterLoading, setFilterLoading] = useState(false);

  const showToast = (message, variant = "success") => {
    setToastVariant(variant);
    setToastMessage(message);
  };

  useEffect(() => {
    const loadNotes = async () => {
      try {
        const [notesResponse, subjectsResponse] = await Promise.all([
          getMyNotes(),
          getMyNotesSubjects().catch((subjectsError) => {
            console.error("Failed to load note subjects", subjectsError);
            return [];
          }),
        ]);
        setNotes(notesResponse);
        setNoteSubjects(subjectsResponse);
      } catch (error) {
        console.error("Failed to load notes", error);
        showToast("We could not load your notes. Please try again.", "error");
      } finally {
        setLoading(false);
      }
    };

    loadNotes();
  }, []);

  const isSearchActive = searchTerm.trim().length > 0;

  useEffect(() => {
    const query = searchTerm.trim();

    if (!query) {
      setSearchResults(null);
      setSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await searchMyNotes(query);
        if (!cancelled) {
          setSearchResults(results);
        }
      } catch (error) {
        console.error("Failed to search notes", error);
        if (!cancelled) {
          setSearchResults([]);
          showToast("We could not search your notes. Please try again.", "error");
        }
      } finally {
        if (!cancelled) {
          setSearchLoading(false);
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (selectedTags.length === 0) {
      setFilteredByTags(null);
      return;
    }

    let cancelled = false;
    const fetchFiltered = async () => {
      setFilterLoading(true);
      try {
        const results = await getFilteredNotesBySubjects(selectedTags);
        if (!cancelled) {
          setFilteredByTags(results);
        }
      } catch (error) {
        console.error("Failed to filter notes by subjects", error);
        if (!cancelled) {
          setFilteredByTags([]);
          showToast("We could not filter your notes. Please try again.", "error");
        }
      } finally {
        if (!cancelled) {
          setFilterLoading(false);
        }
      }
    };

    fetchFiltered();
    return () => { cancelled = true; };
  }, [selectedTags]);

  const availableTags = useMemo(() => {
    const apiSubjectNames = noteSubjects.map((subject) => subject.name);
    const fromNotes = getUniqueTags(notes);
    const apiSet = new Set(apiSubjectNames.map((name) => name.toLowerCase()));
    const extras = fromNotes.filter((tag) => !apiSet.has(tag.toLowerCase()));
    return [...apiSubjectNames, ...extras];
  }, [notes, noteSubjects]);

  const notesForDisplay = searchResults !== null ? searchResults : notes;

  const filteredNotes = useMemo(
    () => {
      if (selectedTags.length > 0) {
        if (searchResults !== null) {
          return filterNotes(searchResults, { searchTerm: "", selectedTags });
        }
        if (filteredByTags !== null) {
          return filteredByTags;
        }
        return [];
      }
      return notesForDisplay;
    },
    [notesForDisplay, selectedTags, filteredByTags, searchResults],
  );

  const groupedSections = useMemo(
    () => groupNotesBySection(filteredNotes),
    [filteredNotes],
  );

  const hasNoNotes = !loading && !isSearchActive && notes.length === 0;
  const hasNoFilteredResults =
    !loading &&
    !searchLoading &&
    !filterLoading &&
    (isSearchActive || notes.length > 0) &&
    filteredNotes.length === 0;

  const handleToggleTag = (tag) => {
    setSelectedTags((currentTags) =>
      currentTags.includes(tag)
        ? currentTags.filter((currentTag) => currentTag !== tag)
        : [...currentTags, tag],
    );
  };

  const handleOpenNote = (note) => {
    setEditNote(null);
    setViewNote(note);
  };

  const handleEditNote = (note) => {
    setViewNote(null);
    setEditNote(note);
  };

  const handleAddNote = () => {
    setViewNote(null);
    setEditNote(createEmptyNoteDraft());
  };

  const handleDeleteNote = async (note, confirmed = false) => {
    if (!confirmed) {
      // open modal to confirm deletion
      setPendingDeleteNote(note);
      return;
    }

    try {
      setDeletingNoteId(note.id);
      await deleteMyNote(note.id);
      setNotes((currentNotes) => currentNotes.filter((currentNote) => currentNote.id !== note.id));
      setSearchResults((currentResults) =>
        currentResults
          ? currentResults.filter((currentNote) => currentNote.id !== note.id)
          : currentResults,
      );
      setViewNote(null);
      setEditNote(null);
      showToast("Your note was deleted.");
    } catch (error) {
      console.error("Failed to delete note", error);
      showToast("We could not delete your note. Please try again.", "error");
    } finally {
      setPendingDeleteNote(null);
      setDeletingNoteId(null);
    }
  };

  const cancelDelete = () => setPendingDeleteNote(null);

  const handleSaveNote = async (noteDraft) => {
    setIsSaving(true);

    const resolvedTitle = deriveNoteTitle(noteDraft.title, noteDraft.content, {
      attachments: noteDraft.attachments,
    });
    const payload = {
      title: resolvedTitle,
      content: noteDraft.content,
      tags: noteDraft.tags?.length ? noteDraft.tags : ["General"],
      attachments: noteDraft.attachments,
      attachmentIdsToRemove: noteDraft.attachmentIdsToRemove,
    };

    try {
      if (noteDraft.id) {
        const updatedNote = await updateMyNote(noteDraft.id, payload);

        setNotes((currentNotes) =>
          currentNotes.map((note) => (note.id === updatedNote.id ? updatedNote : note)),
        );
        setSearchResults((currentResults) =>
          currentResults
            ? currentResults.map((note) => (note.id === updatedNote.id ? updatedNote : note))
            : currentResults,
        );
        setViewNote(updatedNote);
        setEditNote(null);
        showToast("Your note was updated successfully.");
        return;
      }

      const createdNote = await createMyNote({
        title: payload.title,
        content: payload.content,
        tags: payload.tags,
        attachments: payload.attachments,
      });

      setNotes((currentNotes) => [createdNote, ...currentNotes]);
      if (isSearchActive) {
        const results = await searchMyNotes(searchTerm.trim());
        setSearchResults(results);
      }
      setEditNote(null);
      setViewNote(createdNote);
      showToast("Your note was created successfully.");
    } catch (error) {
      console.error("Failed to save note", error);
      showToast("We could not save your note. Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="syllabus-container-mynote my-notes-page">
      <div className="my-notes-shell">

        <div className="my-notes-banner-container">
          <img src="/assets/images/banner_mynotes.svg" alt="My Notes" className="my-notes-banner-img" />
        </div>

        <div className="my-notes-toolbar">
          {/* <h1>My Notes</h1> */}
          <div className="my-notes-toolbar-actions">
            <div className="my-notes-filter-wrapper">
              <button
                type="button"
                className={`my-notes-filter-button ${filterOpen ? "is-active" : ""}`}
                onClick={() => setFilterOpen((open) => !open)}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 6h16M7 12h10M10 18h4" />
                </svg>
              </button>
              {filterOpen ? (
                <NoteFilterPanel
                  tags={availableTags}
                  selectedTags={selectedTags}
                  onToggleTag={handleToggleTag}
                  onClear={() => setSelectedTags([])}
                  onClose={() => setFilterOpen(false)}
                />
              ) : null}
            </div>

            <label className="my-notes-search">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-3.5-3.5" />
              </svg>
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search Subject/ topic"
                aria-label="Search notes"
              />
            </label>
          </div>
        </div>

        {selectedTags.length > 0 ? (
          <div className="my-notes-active-filters">
            <span className="my-notes-active-filters-label">Filters:</span>
            {selectedTags.map((tag) => {
              const tagStyle = getSubjectTagStyle(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  className="my-notes-active-filter-chip"
                  style={{
                    backgroundColor: tagStyle.backgroundColor,
                    color: tagStyle.color,
                    borderColor: tagStyle.borderColor,
                  }}
                  onClick={() => handleToggleTag(tag)}
                >
                  {getTagDisplayLabel(tag)}
                  <span aria-hidden="true">×</span>
                </button>
              );
            })}
            <button
              type="button"
              className="my-notes-active-filter-clear"
              onClick={() => setSelectedTags([])}
            >
              Clear all
            </button>
          </div>
        ) : null}

        <div className={`my-notes-content ${loading ? "" : "is-loaded"}`}>
          {loading ? <NotesSkeleton /> : null}
          {!loading && searchLoading ? (
            <p className="my-notes-empty-state">Searching notes...</p>
          ) : null}
          {!loading && !searchLoading && filterLoading ? (
            <p className="my-notes-empty-state">Filtering notes...</p>
          ) : null}

          {hasNoNotes ? <MyNotesEmptyState onAddNote={handleAddNote} /> : null}

          {hasNoFilteredResults ? (
            <p className="my-notes-empty-state">No notes match your search or filters.</p>
          ) : null}

          {!loading && !searchLoading && !hasNoNotes && !hasNoFilteredResults
            ? groupedSections.map((section, index) => (
                <NoteSection
                  key={section.sectionLabel}
                  sectionLabel={section.sectionLabel}
                  notes={section.notes}
                  showAddCard={index === 0}
                  layout={"carousel"}
                  onAddNote={handleAddNote}
                  onOpenNote={handleOpenNote}
                  onEditNote={handleEditNote}
                  onDeleteNote={handleDeleteNote}
                />
              ))
            : null}
        </div>
      </div>

      <ViewNoteModal
        note={viewNote}
        onClose={() => setViewNote(null)}
        onEdit={handleEditNote}
      />

      <EditNoteModal
        note={editNote}
        isSaving={isSaving}
        subjects={noteSubjects}
        subjectsLoading={loading}
        onClose={() => setEditNote(null)}
        onSave={handleSaveNote}
      />

      {pendingDeleteNote ? (
        <div
          role="dialog"
          aria-modal="true"
          className="notes-confirm-overlay"
          onMouseDown={cancelDelete}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2500,
          }}
        >
          <div
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 8,
              padding: 18,
              minWidth: 320,
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            }}
          >
            <p style={{ margin: 0, marginBottom: 12, fontWeight: 600 }}>
              Delete "{deriveNoteTitle(pendingDeleteNote.title, pendingDeleteNote.content, {
                attachments: pendingDeleteNote.attachments,
              })}"?
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                type="button"
                onClick={cancelDelete}
                style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #e1e1e6", background: "white" }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteNote(pendingDeleteNote, true)}
                disabled={deletingNoteId === pendingDeleteNote?.id}
                style={{
                  padding: "8px 12px",
                  borderRadius: 6,
                  border: "none",
                  background: "#d9534f",
                  color: "white",
                  cursor: deletingNoteId === pendingDeleteNote?.id ? "not-allowed" : "pointer",
                  opacity: deletingNoteId === pendingDeleteNote?.id ? 0.7 : 1,
                }}
              >
                {deletingNoteId === pendingDeleteNote?.id ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <NoteToast
        message={toastMessage}
        variant={toastVariant}
        onClose={() => setToastMessage("")}
      />
    </div>
  );
}

export default Mynotes;
